<template>
    <Page class="overview">
        <div class="header">
            Observability made simple
        </div>

        <div class="subheader">
            An open-source observability platform built for simplicity.
            Say goodbye to manual analysis of metrics, logs, and traces.
            Gain actionable insights and focus on remediation.
        </div>

        <div class="shortcuts">
            <v-btn v-for="(name, id) in shortcuts" :to="{hash: '#'+id}" outlined class="shortcut">
                {{name}}
            </v-btn>
        </div>

        <v-row class="feature" id="zero-instrumentation">
            <v-col cols="12" md="4" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Zero-instrumentation
                </div>
                <div class="description">
                    Coroot uses eBPF to automatically collect comprehensive telemetry data, including metrics, logs, and traces.
                    It provides a detailed Service Map of the entire system, eliminating blind spots.
                    Coroot also includes predefined inspections that can identify root causes for over 80% of issues without requiring any configuration.
                </div>
            </v-col>
            <v-col cols="12" md="8" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/service_map.png" />
                </v-card>
            </v-col>
        </v-row>

        <v-row class="feature" id="app-health-summary">
            <v-col cols="12" md="8" order="2" order-md="1" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/app_health_summary.png" />
                </v-card>
            </v-col>
            <v-col cols="12" md="4" order="1" order-md="2" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Application Health Summary
                </div>
                <div class="description">
                    Coroot's Application Health Summary feature makes it easy to understand the status of your services, even when you have hundreds of them.
                    It provides instant insights into application logs and tracks Service Level Objectives (SLOs) for efficient performance monitoring.
                </div>
            </v-col>
        </v-row>

        <v-row class="feature" id="distributed-tracing">
            <v-col cols="12" md="4" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Distributed Tracing
                </div>
                <div class="description">
                    Coroot's distributed tracing helps you answer critical questions about your system's performance:

                    <ul class="mt-2">
                        <li>
                            Why are some requests failing?
                        </li>
                        <li>
                            Why are certain requests taking longer than expected?
                        </li>
                    </ul>
                    Coroot fully supports OpenTelemetry, ensuring no vendor lock-in.
                    Struggling to instrument legacy or third-party services?
                    Coroot's eBPF-based instrumentation captures requests without any code changes.
                </div>

                <div class="mt-3">
                    <v-btn outlined href="/blog/coroot-v1-0-revolutionizing-distributed-tracing-analysis" target="_blank">Learn more</v-btn>
                </div>
            </v-col>
            <v-col cols="12" md="8" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/distributed_tracing.png" />
                </v-card>
            </v-col>
        </v-row>

        <v-row class="feature" id="log-monitoring">
            <v-col cols="12" md="8" order="2" order-md="1" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/log_monitoring.png" />
                </v-card>
            </v-col>
            <v-col cols="12" md="4" order="1" order-md="2" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Log Monitoring
                </div>
                <div class="description">
                    Grasp insights from logs with just a quick glance.
                    Coroot performs low-overhead log analysis right on the node to identify message severities and recurring patterns.
                    This process is seamless and compatible with a wide range of log formats, providing valuable meta-information for quick and easy log analysis.
                </div>
                <div class="mt-3">
                    <v-btn outlined href="/docs/coroot-community-edition/logs/overview" target="_blank">Learn more</v-btn>
                </div>
            </v-col>
        </v-row>

        <v-row class="feature" id="continuous-profiling">
            <v-col cols="12" md="4" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Continuous Profiling
                </div>
                <div class="description">
                    Coroot's Continuous Profiling allows you easily identify and analyze any unexpected spikes in CPU and memory usage down to the precise line of code.
                    This allows you to quickly pinpoint and resolve performance bottlenecks, optimize your application's resource utilization, and deliver a faster and more reliable user experience.
                </div>
                <div class="mt-3">
                    <v-btn outlined href="/docs/coroot-community-edition/profiling/overview" target="_blank">Learn more</v-btn>
                </div>
            </v-col>
            <v-col cols="12" md="8" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/docs/coroot-ce/profiling/profile-diff.png" />
                </v-card>
            </v-col>
        </v-row>

        <v-row class="feature" id="deployment-tracking">
            <v-col cols="12" md="8" order="2" order-md="1" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/docs/coroot-ce/inspections/deployments.png" />
                </v-card>
            </v-col>
            <v-col cols="12" md="4" order="1" order-md="2" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Deployment Tracking
                </div>
                <div class="description">
                    Coroot discovers and monitors every application rollout in your Kubernetes cluster.
                    Each release is automatically compared with the previous one, so you'll never miss even the slightest performance degradation.
                    No integration with your CI/CD pipeline is required.
                </div>
                <div class="mt-3">
                    <v-btn outlined href="/docs/coroot-community-edition/inspections/deployment-tracking" target="_blank">Learn more</v-btn>
                </div>
            </v-col>
        </v-row>

        <v-row class="feature" id="expertise">
            <v-col cols="12" md="4" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Built-in Expertise
                </div>
                <div class="description">
                    Coroot can automatically identify the root causes of over 80% of issues.
                    If an app is not meeting its Service Level Objectives (SLOs), Coroot will send a single alert that includes the results of all relevant inspections
                    You can easily adjust any inspection for a particular application or an entire project.
                </div>
                <div class="mt-3">
                    <v-btn outlined href="/docs/coroot-community-edition/inspections/overview" target="_blank">Learn more</v-btn>
                </div>
            </v-col>
            <v-col cols="12" md="8" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/slack_alert.png" />
                </v-card>
            </v-col>
        </v-row>

        <v-row class="feature" id="cost-monitoring">
            <v-col cols="12" md="8" order="2" order-md="1" class="px-4">
                <v-card class="pa-3">
                    <ImageWrapper src="/static/img/cost_monitoring.png" />
                </v-card>
            </v-col>
            <v-col cols="12" md="4" order="1" order-md="2" class="d-flex flex-column justify-center px-4">
                <div class="name">
                    Cost Monitoring
                </div>
                <div class="description">
                    Every developer understands that in order to optimize something, you must first measure it.
                    The same principle applies to you cloud costs - measuring them is crucial for optimization.
                    Now you can easily monitor you cloud costs with Coroot. It doesn't require access to you cloud account or any other configurations.
                </div>
                <div class="mt-3">
                    <v-btn outlined href="/docs/coroot-community-edition/costs/overview" target="_blank">Learn more</v-btn>
                </div>
            </v-col>
        </v-row>

        <Banner />

    </Page>
</template>

<script>
import Page from "@/pages/Page";
import Banner from "@/components/Banner.vue";

export default {
    components: {Page, Banner},

    computed: {
        shortcuts() {
            return {
                'zero-instrumentation': 'Zero-instrumentation',
                'app-health-summary': 'Application Health Summary',
                'distributed-tracing': 'Distributed Tracing',
                'log-monitoring': 'Log Monitoring',
                'continuous-profiling': 'Continuous Profiling',
                'deployment-tracking': 'Deployment Tracking',
                'expertise': 'Built-in Expertise',
                'cost-monitoring': 'Cost Monitoring',
            };
        },
    },
};
</script>

<style scoped>
.overview {
    font-family: Poppins, sans-serif;
}

.header {
    font-size: 30px;
    font-weight: 600;
    text-align: center;
    margin-top: 40px;
}
.subheader {
    font-size: 24px;
    line-height: 40px;
    font-weight: 400;
    margin-top: 36px;
    margin-bottom: 12px;
    text-align: center;
}

.shortcuts {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin: 40px 0;
}
.shortcut {
    font-weight: 500 !important;
    padding: 0 8px !important;
}

.feature {
    margin-top: 60px;
}
.feature .name {
    font-size: 30px;
    line-height: 36px;
    font-weight: 600;
    margin-bottom: 20px;
}
.feature .description {
    font-size: 16px;
    line-height: 24px;
}


@media (min-width: 600px) {
    .header {
        font-size: 40px;
        margin-top: 80px;
    }
    .feature {
        margin-top: 80px;
    }
    .shortcuts {
        gap: 24px;
    }
    .shortcut {
        padding: 0 16px !important;
    }
}
@media (min-width: 960px) {
    .header {
        font-size: 60px;
        margin-top: 96px;
    }
    .feature {
        margin-top: 100px;
    }
}
</style>
